import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Maximize2, Minimize2, ArrowLeft, Gavel, CheckCircle2, Clock, MonitorPlay, Trophy, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSiteLogo } from "@/lib/siteConfig";

const MANAGER_ROLES = ["admin", "segreteria", "direttore"];
const discLabel = { artistica: "Ginnastica Artistica", ritmica: "Ginnastica Ritmica" };
const POLL_MS = 2500;

export default function DirectorLive() {
  const { user, loading: authLoading } = useAuth();
  const logoUrl = useSiteLogo();
  const [judges, setJudges] = useState([]);
  const [activeJudges, setActiveJudges] = useState(0);
  const [pedana, setPedana] = useState(null);
  const [scores, setScores] = useState([]);
  const [athleteMap, setAthleteMap] = useState({});
  const [apparatus, setApparatus] = useState("");
  const [fs, setFs] = useState(false);
  const [ready, setReady] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const flashRef = useRef({});
  const [, force] = useState(0);

  const isManager = MANAGER_ROLES.includes(user?.role);

  useEffect(() => {
    if (!isManager) return;
    api.get("/athletes").then((r) => {
      const m = {}; r.data.forEach((a) => { m[a.id] = a; }); setAthleteMap(m);
    }).catch(() => {}).finally(() => setReady(true));
  }, [isManager]);

  // Poll pedana
  useEffect(() => {
    if (!isManager) return;
    const load = () => api.get("/live/pedana").then((r) => setPedana(r.data)).catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [isManager]);

  const currentId = pedana?.current?.id || "";

  // Adatta n. giudici attesi ed elenco giudici alla gara dell'atleta in pedana
  useEffect(() => {
    if (!isManager) return;
    const params = currentId ? { athlete_id: currentId } : {};
    api.get("/scores/config", { params }).then((r) => setActiveJudges(r.data.active_judges)).catch(() => {});
    api.get("/scores/judges", { params }).then((r) => setJudges(r.data)).catch(() => {});
  }, [currentId, isManager]);

  // Poll scores for current athlete
  const loadScores = useCallback(() => {
    if (!currentId) { setScores([]); return; }
    api.get("/scores", { params: { athlete_id: currentId } }).then((r) => setScores(r.data)).catch(() => {});
  }, [currentId]);
  useEffect(() => {
    if (!isManager) return;
    loadScores();
    const t = setInterval(loadScores, POLL_MS);
    return () => clearInterval(t);
  }, [loadScores, isManager]);

  // Attrezzi disponibili per l'atleta corrente
  const apparatusOptions = useMemo(() => {
    const a = athleteMap[currentId];
    return a?.apparatus?.length ? a.apparatus : [];
  }, [athleteMap, currentId]);

  // Reset attrezzo quando cambia atleta
  useEffect(() => {
    setApparatus(apparatusOptions.length ? apparatusOptions[0] : "");
  }, [currentId, apparatusOptions]);

  const currentScores = useMemo(
    () => scores.filter((s) => (s.apparatus || "") === (apparatus || "")),
    [scores, apparatus]
  );

  const statusFor = (jid) => {
    const s = currentScores.find((x) => x.judge_id === jid);
    if (!s) return { status: "empty" };
    return { status: s.confirmed ? "confirmed" : "draft", total: s.total };
  };

  // Rileva "arrivo" di un voto per accendere il riquadro
  useEffect(() => {
    const now = {};
    judges.forEach((j) => { now[j.id] = statusFor(j.id).status; });
    let changed = false;
    Object.keys(now).forEach((jid) => {
      if (flashRef.current[jid] !== now[jid] && now[jid] !== "empty") {
        flashRef.current[`flash_${jid}`] = Date.now();
        changed = true;
      }
      flashRef.current[jid] = now[jid];
    });
    if (changed) {
      force((n) => n + 1);
      setTimeout(() => force((n) => n + 1), 1400);
    }
  }, [currentScores, judges]);

  const confirmedList = judges.filter((j) => statusFor(j.id).status === "confirmed");
  const sumTotal = confirmedList.reduce((acc, j) => acc + (statusFor(j.id).total || 0), 0);
  const expected = activeJudges || judges.length || 7;
  const complete = confirmedList.length >= expected && expected > 0;

  const toggleFs = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen?.(); setFs(true); }
    else { document.exitFullscreen?.(); setFs(false); }
  };

  const advanceNext = async () => {
    if (!pedana?.next?.id) return;
    setAdvancing(true);
    try {
      const { data } = await api.put("/live/pedana", { current_id: pedana.next.id, event_id: pedana?.event_id || "all" });
      setPedana(data);
      toast.success(`In pedana: ${data.current?.name || "prossimo atleta"}`);
    } catch (e) { toast.error("Impossibile avanzare"); }
    finally { setAdvancing(false); }
  };

  if (authLoading) return <div className="min-h-screen bg-[#020617] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isManager) return <Navigate to="/app" replace />;

  const cur = pedana?.current;
  const nxt = pedana?.next;

  return (
    <div className="min-h-screen bg-[#020617] text-foreground noise-bg flex flex-col" data-testid="director-live">
      {/* Barra controlli */}
      <div className="sticky top-0 z-10 bg-[#020617]/90 backdrop-blur border-b border-white/10 px-6 py-3 flex flex-wrap items-center gap-3">
        <Link to="/app?section=punteggi" data-testid="director-live-back" className="text-muted-foreground hover:text-gold inline-flex items-center gap-1.5 text-sm"><ArrowLeft className="w-4 h-4" /> Esci</Link>
        <div className="h-5 w-px bg-white/10" />
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gold"><Gavel className="w-4 h-4" /> Vista Direttore · Live</span>
        {apparatusOptions.length > 1 && (
          <div className="inline-flex rounded-lg border border-white/10 bg-[#0F172A] p-1 ml-2">
            {apparatusOptions.map((ap) => (
              <button key={ap} data-testid={`director-app-${ap}`} onClick={() => setApparatus(ap)} className={`px-3 py-1 rounded text-sm font-medium capitalize transition-colors ${apparatus === ap ? "bg-gold text-[#020617]" : "text-muted-foreground hover:text-foreground"}`}>{ap}</button>
            ))}
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          {logoUrl && <img src={logoUrl} alt="logo" className="h-8 opacity-90" />}
          <button data-testid="director-live-fs" onClick={toggleFs} className="text-muted-foreground hover:text-gold inline-flex items-center gap-1.5 text-sm border border-white/10 rounded-lg px-3 py-1.5">
            {fs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />} {fs ? "Riduci" : "Schermo intero"}
          </button>
        </div>
      </div>

      {!ready ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
      ) : !cur ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="director-live-waiting">
          <MonitorPlay className="w-16 h-16 text-amber-400/70 mb-5" />
          <h2 className="font-display text-3xl md:text-4xl text-foreground">In attesa dell'atleta in pedana</h2>
          <p className="text-muted-foreground mt-3 max-w-lg">Imposta l'atleta corrente dalla sezione <b>Programma</b>. I 7 riquadri dei giudici si accenderanno qui man mano che arrivano i voti.</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col px-6 py-6 max-w-[1400px] mx-auto w-full">
          {/* Atleta in pedana */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold mb-2">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span></span>
              Atleta in pedana
            </div>
            <h1 data-testid="director-current-athlete" className="font-display text-4xl md:text-6xl text-foreground leading-tight">{cur.name}</h1>
            <p className="text-muted-foreground mt-2 text-base md:text-lg">{cur.sub}{cur.discipline ? ` · ${discLabel[cur.discipline] || cur.discipline}` : ""}{apparatus ? ` · ${apparatus}` : ""}</p>
            {nxt && <p className="text-sm text-muted-foreground/70 mt-1">Prossimo: <span className="text-foreground/80">{nxt.name}</span></p>}
          </div>

          {/* Riquadri giudici */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 content-start">
            {judges.map((j, i) => {
              const st = statusFor(j.id);
              const flashed = flashRef.current[`flash_${j.id}`] && Date.now() - flashRef.current[`flash_${j.id}`] < 1400;
              const base = "rounded-2xl border p-5 flex flex-col items-center justify-center text-center min-h-[150px] transition-colors duration-300";
              const style = st.status === "confirmed"
                ? "border-[#10B981]/50 bg-[#10B981]/10"
                : st.status === "draft"
                ? "border-amber-500/50 bg-amber-500/10"
                : "border-white/10 bg-white/[0.03]";
              return (
                <motion.div
                  key={j.id}
                  data-testid={`judge-box-${j.id}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0, scale: flashed ? [1, 1.05, 1] : 1, boxShadow: flashed ? "0 0 0 3px rgba(16,185,129,0.5)" : "0 0 0 0 rgba(0,0,0,0)" }}
                  transition={{ delay: i * 0.05, duration: flashed ? 0.6 : 0.4 }}
                  className={`${base} ${style}`}
                >
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <Gavel className="w-3.5 h-3.5" /> {j.name}
                  </div>
                  {st.status === "empty" ? (
                    <div className="text-muted-foreground/60 text-sm mt-3">In attesa…</div>
                  ) : (
                    <>
                      <div data-testid={`judge-box-total-${j.id}`} className={`font-display text-4xl md:text-5xl ${st.status === "confirmed" ? "text-[#10B981]" : "text-amber-300"}`}>{Number(st.total).toFixed(3)}</div>
                      <div className={`mt-2 inline-flex items-center gap-1 text-xs ${st.status === "confirmed" ? "text-[#10B981]" : "text-amber-400"}`}>
                        {st.status === "confirmed" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Confermato</> : <><Clock className="w-3.5 h-3.5" /> Bozza</>}
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Riepilogo */}
          <div className="mt-6 rounded-2xl border border-white/10 bg-[#0F172A] p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <span className="text-sm text-muted-foreground" data-testid="director-progress">{confirmedList.length}/{expected} giudici confermati</span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">Totale attrezzo</span>
                  <span data-testid="director-total" className={`font-display text-4xl ${complete ? "text-gold" : "text-foreground/80"}`}>{sumTotal.toFixed(3)}</span>
                  {complete && <span className="inline-flex items-center gap-1 text-[#10B981] text-sm"><Trophy className="w-4 h-4" /> Completo</span>}
                </div>
                <Button
                  data-testid="director-advance-next"
                  onClick={advanceNext}
                  disabled={!nxt || advancing}
                  className={`font-semibold ${complete && nxt ? "bg-gold text-[#020617] hover:bg-[#FDE047] animate-pulse" : "bg-white/5 text-foreground border border-white/15 hover:bg-white/10"}`}
                >
                  {advancing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-1.5" />}
                  {nxt ? "Avanza al prossimo atleta" : "Ultimo atleta"}
                </Button>
              </div>
            </div>
            {complete && nxt && <p className="text-xs text-[#10B981] mb-2" data-testid="director-advance-hint">Tutti i {expected} voti confermati — puoi passare a {nxt.name}.</p>}
            <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#10B981] to-gold"
                initial={{ width: 0 }}
                animate={{ width: `${expected ? (confirmedList.length / expected) * 100 : 0}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
